import { AutoComplete, Col, Input, Row } from 'antd';
import debounce from 'lodash.debounce';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useState } from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { Button, notification } from 'ui';
import { searchPartRequest } from '../containers/parts-actions';
// eslint-disable-next-line max-len
import { clearPartField } from '../../../tickets/containers/smart-action-sidepanel/smart-action-sidepanel-actions';

const HighlightSpan = styled.span`
  > .highlight {
    color: ${p => p.theme.colors.theme};
    font-weight: ${p => p.theme.fontWeight.bold};
  }
`;

const StyledAutoComplete = styled(AutoComplete)`
  font-size: 16px;
  font-weight: 400;
  width: 100%;
  .ant-select-selection-search-input {
    height: 32px;
  }
  input {
    height: auto !important;
  }
`;

const StyledTextarea = styled(Input.TextArea)`
  resize: none !important;
  height: 34px !important;
  line-height: 16px !important;
`;

const StyledRow = styled(Row)`
  margin-top: 10px;
`;

const SearchPart = ({
  disabled,
  intl,
  handleAddPart,
  handleUpdateParts,
  selectedMaterials,
  setSelectedMaterials,
  materialNumbers,
  setMaterialNumbers,
  widthSearchField = 12,
  widthButton = 8,
  checkUpdate = false,
  serviceCenterId = null,
}) => {
  const {
    materials,
    isSearching,
    isAdding,
    forceClearSearch,
  } = useSelector(state => state.order.parts);
  const [copyFromExternalSource, setCopyFromExternalSource] = useState(false);
  const [copiedEntries, setCopiedEntries] = useState({});
  const dispatch = useDispatch();

  useEffect(() => {
    if (checkUpdate && copiedEntries && Object.keys(copiedEntries)?.length) {
      handleUpdateParts(copiedEntries);
      setCopiedEntries({});
    }
  }, [checkUpdate]);

  useEffect(() => {
    if (forceClearSearch) {
      setMaterialNumbers([]);
      setCopyFromExternalSource(false);
      dispatch(clearPartField(false));
    }
  }, [forceClearSearch]);

  useEffect(() => {
    if (!isSearching && copyFromExternalSource) {
      const selectedMaterialsNew = materials.reduce((mats, mat) => {
        const [preferredVendor] = mat.vendors;
        return [
          ...mats,
          {
            id: `${mat.number}_${preferredVendor ? preferredVendor.vendorId : '0'}`,
            materialNumber: mat.number,
            vendorId: preferredVendor ? preferredVendor.vendorId : null,
            quantity: mat.quantity || 1,
            description: mat.description,
            weight: mat.weight,
            weightUnit: mat.weightUnit,
            cost: mat?.cost,
            costCurrency: mat?.costCurrency,
            isObsolete: mat?.isObsolete,
          },
        ];
      }, []);
      setSelectedMaterials(selectedMaterialsNew);
    }
  }, [isSearching]);

  const handleSearch = useCallback(
    debounce((searchValue) => {
      if (!searchValue) {
        setSelectedMaterials([]);
        setCopiedEntries({});
        setCopyFromExternalSource(false);
        return;
      }
      // Detect copy/paste from excel or ids with ;
      if (/^\s+|\s+$|;|\s*;\s*/g.test(searchValue)) {
        const rows = /;/g.test(searchValue) ? searchValue.split(';')?.filter(row => row.trim())
          : searchValue.split('\n')?.filter(row => row.trim());
        if (rows?.length) {
          const copiedEntriesNew = rows?.map((row) => {
            const [id, amount] = row.split('\t');
            const convertedAmount = parseFloat(amount);
            if (!isNaN(convertedAmount) && isFinite(convertedAmount)) {
              return { number: id, quantity: convertedAmount };
            }
            // default to 1 if no amount is present
            return { number: id, quantity: 1 };
          });
          setCopiedEntries(copiedEntriesNew);
          setCopyFromExternalSource(true);
          dispatch(searchPartRequest({
            serviceCenterId, items: copiedEntriesNew,
          }));
        }
      } else {
        dispatch(searchPartRequest({
          serviceCenterId,
          searchValue: searchValue.replace(/\s/g, ''),
        }));
        setCopyFromExternalSource(false);
      }
    },
      300),
    [searchPartRequest],
  );

  const splitMaterialsPerVendor = mats =>
    mats.reduce((acc, curr) => {
      if (curr.vendors && curr.vendors.length) {
        return [
          ...acc,
          ...curr.vendors?.map(v => ({
            id: `${curr.number}_${v.vendorId}`,
            materialNumber: curr.number,
            vendorId: v.vendorId,
            description: `(${curr.number}) ${curr.description} - (${v.vendorId}) ${v.name}`,
            label: `${curr.highlightedDescription} - (${v.vendorId}) ${v.name}`,
            weight: curr?.weight,
            weightUnit: curr?.weightUnit,
            quantity: curr?.quantity,
            isObsolete: curr?.isObsolete,
            cost: curr?.cost,
            costCurrency: curr?.costCurrency,
          })),
        ];
      }
      return [
        ...acc,
        {
          id: curr.number,
          materialNumber: curr.number,
          vendorId: null,
          description: `(${curr.number}) ${curr.description}`,
          label: curr.highlightedDescription,
          weight: curr?.weight,
          weightUnit: curr?.weightUnit,
          quantity: curr?.quantity,
          isObsolete: curr?.isObsolete,
          cost: curr?.cost,
          costCurrency: curr?.costCurrency,
        },
      ];
    }, []);

  const handlePartSelect = (p, option) => {
    const selectedMaterial = splitMaterialsPerVendor(materials)?.find(
      m => m.id === option.key,
    );
    setSelectedMaterials([selectedMaterial]);
  };

  const materialsByVendor = splitMaterialsPerVendor(materials);
  const materialOptions = materialsByVendor?.map(m => ({
    key: m.id,
    value: m.description,
    label: <HighlightSpan dangerouslySetInnerHTML={{ __html: m.label }} />,
  }),
  );

  const handleMaterialNumbersChange = (matNumbers) => {
    setMaterialNumbers(matNumbers);
  };

  const handlePartAdd = () => {
    const [notObsolete, obsolete] = selectedMaterials?.reduce(
      ([notObs, obs], material) =>
        material?.isObsolete ?
          [notObs, [...obs, material]] :
          [[...notObs, material], obs],
      [[], []],
    );

    if (obsolete?.length) {
      notification.warning({
        key: 'cannotAddObsoleteParts',
        placement: 'top',
        message: intl?.formatMessage(
          { id: 'order.parts.supersededParts.CANNOT_ADD_OBSOLETE_PARTS' },
          {
            parts: obsolete?.map(o =>
              `${o?.materialNumber}${o?.description ? ` - ${o.description}` : ''}`).join(', '),
          },
        ),
      });
    }
    handleAddPart(notObsolete);
    setSelectedMaterials([]);
  };

  const renderAddButtonText = () => {
    if (copyFromExternalSource) {
      if (isSearching || isAdding) {
        if (Object.keys(copiedEntries).length > 1) {
          return (
            <FormattedMessage
              id='order.parts.ADD_MULTIPLE'
              values={{ amount: Object.keys(copiedEntries).length }}
            />
          );
        }
        return (
          <FormattedMessage
            id='order.parts.ADD_ONE'
          />
        );
      }

      if (selectedMaterials?.length) {
        if (selectedMaterials.length > 1) {
          return (
            <FormattedMessage
              id='order.parts.ADD_MULTIPLE'
              values={{ amount: selectedMaterials.length }}
            />
          );
        }
        return (
          <FormattedMessage
            id='order.parts.ADD_ONE'
          />
        );
      }
      return <FormattedMessage id='order.parts.NO_MATERIALS_FOUND' />;
    }
    return <FormattedMessage id='order.parts.ADD' />;
  };

  return (
    <StyledRow type='flex' gutter={16}>
      <Col span={widthSearchField}>
        <StyledAutoComplete
          options={copyFromExternalSource ? [] : materialOptions}
          onSelect={handlePartSelect}
          onSearch={handleSearch}
          disabled={disabled}
          onChange={handleMaterialNumbersChange}
          value={materialNumbers}
        >
          <StyledTextarea
            placeholder={intl.formatMessage({
              id: 'order.parts.ADD_MATERIAL',
            })}
          />
        </StyledAutoComplete>
      </Col>
      <Col span={widthButton}>
        <Button
          type='secondary'
          disabled={!selectedMaterials || !selectedMaterials.length || disabled}
          onClick={handlePartAdd}
          loading={isAdding || isSearching}
        >
          {renderAddButtonText()}
        </Button>
      </Col>
    </StyledRow>
  );
};

SearchPart.propTypes = {
  disabled: PropTypes.bool,
  intl: PropTypes.object,
  handleUpdateParts: PropTypes.func,
  handleAddPart: PropTypes.func,
  selectedMaterials: PropTypes.object,
  setSelectedMaterials: PropTypes.func,
  materialNumbers: PropTypes.array,
  setMaterialNumbers: PropTypes.func,
  widthSearchField: PropTypes.number,
  widthButton: PropTypes.number,
  checkUpdate: PropTypes.bool,
  serviceCenterId: PropTypes.string,
};

export default injectIntl(SearchPart);
